import { setBlockType } from "prosemirror-commands";
import { MenuItem, type MenuItemSpec } from "prosemirror-menu";
import type { Attrs, Node, NodeType } from "prosemirror-model";
import type { NodeSelection, Selection } from "prosemirror-state";

/**
 * Check whether the node in the given selection has markup that corresponds
 * to the given type, attributes, and marks.
 *
 * @param selection A `Selection` object. If it is a `NodeSelection`,
 *   the selected node is checked. Otherwise, all top-level nodes of the selection
 *   are checked, without recursing into their children.
 * @param doc The ProseMirror document
 * @param nodeType The node type to check for
 * @param attrs Optional attributes to check for
 * @returns `true` if all selected nodes have the given markup; `false` otherwise
 */
function doSelectedNodesHaveMarkup(
  selection: Selection,
  doc: Node,
  nodeType: NodeType,
  attrs?: Attrs | null,
): boolean {
  if ((selection as NodeSelection).node) {
    return (selection as NodeSelection).node.hasMarkup(nodeType, attrs);
  }

  let allNodesHaveMarkup = true;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (!node.hasMarkup(nodeType, attrs)) {
      allNodesHaveMarkup = false;
    }
    // Never recurse into child nodes; otherwise, we check text nodes, too.
    return false;
  });
  return allNodesHaveMarkup;
}

/**
 * This is based on `blockTypeItem` from `prosemirror-menu`,
 * but it toggles between the primary node type and the secondary (e.g., paragraph).
 * `options.attrs` may be an object to provide the attributes for the changed node.
 */
export function toggleBlockTypeItem(
  primaryNodeType: NodeType,
  secondaryNodeType: NodeType,
  options: Partial<MenuItemSpec> & { attrs?: Attrs | null },
) {
  const passedOptions: MenuItemSpec = {
    run: (state, dispatch) => {
      const isPrimaryType = doSelectedNodesHaveMarkup(
        state.selection,
        state.doc,
        primaryNodeType,
        options.attrs,
      );
      const command = setBlockType(
        isPrimaryType ? secondaryNodeType : primaryNodeType,
        isPrimaryType ? null : options.attrs,
      );
      return command(state, dispatch);
    },
    active(state) {
      return doSelectedNodesHaveMarkup(state.selection, state.doc, primaryNodeType, options.attrs);
    },
  };
  Object.assign(passedOptions, options);
  return new MenuItem(passedOptions);
}
